'use client'

// Rain — the weather half of WEATHER = MOOD. One instancedMesh of thin
// quads animated ENTIRELY in the vertex shader (TSL positionNode): each
// instance hashes to a column in a cylinder over the town and falls with
// mod-wrap. Zero per-frame JS, one draw call; opacity rides mood.wetness
// so rain fades in as the world's state sours (danger / low vitality) and
// dries up under abundance. Hidden entirely when dry.

import { useMemo, useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three/webgpu'
import {
  Fn, float, vec3, vec4, instanceIndex, hash, time, mod, positionLocal,
} from 'three/tsl'
import { mood, moodState } from '../../../lib/world/mood'

const COUNT = 1400
const AREA_RADIUS = 85     // covers the town
const FALL_HEIGHT = 46
const BASE_SPEED = 26

// Props let the content studio tighten the rain around a single subject
// (smaller areaRadius = denser near the bird). Defaults reproduce the
// original town-wide rain exactly, so existing mounts are unaffected.
export function Rain({ count = COUNT, areaRadius = AREA_RADIUS, fallHeight = FALL_HEIGHT, baseSpeed = BASE_SPEED } = {}) {
  const groupRef = useRef(null)
  const meshRef = useRef(null)

  const { geometry, material } = useMemo(() => {
    // a thin vertical quad; stylised streak (no billboarding needed at
    // this width — reads as rain from every sensible camera)
    const geometry = new THREE.PlaneGeometry(0.035, 0.85)
    const material = new THREE.MeshBasicNodeMaterial({
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
    })

    const posNode = Fn(() => {
      const h1 = hash(instanceIndex)
      const h2 = hash(instanceIndex.add(1013))
      const h3 = hash(instanceIndex.add(7177))

      // column position: uniform-ish disc via sqrt
      const angle = h1.mul(6.28318)
      const radius = h2.sqrt().mul(areaRadius)
      const x = angle.cos().mul(radius)
      const z = angle.sin().mul(radius)

      // fall: wrap y through [0, fallHeight), speed varies per drop —
      // heavier rain (wetness) also falls faster
      const speed = float(baseSpeed).mul(h3.mul(0.6).add(0.7)).mul(mood.wetness.mul(0.6).add(0.7))
      const y = mod(h3.mul(fallHeight).sub(time.mul(speed)), fallHeight)

      return positionLocal.add(vec3(x, y, z))
    })
    material.positionNode = posNode()

    // fade with wetness + fade drops near the ground
    material.opacityNode = Fn(() => {
      const h3 = hash(instanceIndex.add(7177))
      const speed = float(baseSpeed).mul(h3.mul(0.6).add(0.7)).mul(mood.wetness.mul(0.6).add(0.7))
      const y = mod(h3.mul(fallHeight).sub(time.mul(speed)), fallHeight)
      const groundFade = y.div(4.0).clamp(0.0, 1.0)
      return mood.wetness.mul(0.5).mul(groundFade)
    })()
    material.colorNode = vec4(vec3(0.75, 0.82, 0.95), float(1.0)).xyz

    return { geometry, material }
  }, [count, areaRadius, fallHeight, baseSpeed])

  // Skip the draw entirely when dry. Exception: stay visible for the first
  // moments after mount (drops render at opacity 0 when wetness is 0) so the
  // rain material's pipeline compiles during load. Its FIRST render otherwise
  // happens the instant weather turns mid-session, and a fresh WebGPU
  // pipeline compile mid-play is a visible hitch.
  const warmupUntil = useRef(0)
  useFrame(() => {
    if (!groupRef.current) return
    if (!warmupUntil.current) warmupUntil.current = performance.now() + 1500
    const warming = performance.now() < warmupUntil.current
    const wet = moodState.wetness
    groupRef.current.visible = warming || wet > 0.03
    // Draw count tracks how hard it's raining. Nights drizzle by design
    // (night unease feeds wetness), so the common case is wetness ~0.1-0.2:
    // a few hundred streaks, not the full storm's worth of transparent
    // double-sided quads. Full count returns as real weather rolls in.
    if (meshRef.current) {
      meshRef.current.count = warming
        ? count
        : Math.min(count, Math.max(0, Math.ceil(count * Math.min(1, wet * 2.2))))
    }
  })

  return (
    <group ref={groupRef} visible={false}>
      <instancedMesh ref={meshRef} args={[geometry, material, count]} frustumCulled={false} />
    </group>
  )
}

export default Rain
